/**
 * ProfilePage - User profile with loyalty points and queue history (CLIENT VERSION)
 * For barbers, redirects to BarberProfilePage
 * For guests, shows simplified view with option to create account
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, limit, getDocs, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/lib/toast';
import BarberProfilePage from './BarberProfilePage';
import DeleteAccountModal from '@/components/modals/DeleteAccountModal';
import ImageCropModal from '@/components/modals/ImageCropModal';
import GuestProfileView from '@/components/profile/GuestProfileView';
import LargeTextToggleCard from '@/components/profile/LargeTextToggleCard';
import ProfileHeader from '@/components/profile/ProfileHeader';
import LinkedAccountsSection from '@/components/profile/LinkedAccountsSection';
import EditProfileSection from '@/components/profile/EditProfileSection';
import NotificationPreferencesSection from '@/components/profile/NotificationPreferencesSection';
import LoyaltyPointsCard from '@/components/profile/LoyaltyPointsCard';
import QueueHistorySummary from '@/components/profile/QueueHistorySummary';
import LoyaltyTransactionsHistory, { LoyaltyTransaction } from '@/components/profile/LoyaltyTransactionsHistory';
import PointsInfoCard from '@/components/profile/PointsInfoCard';
import DangerZoneSection from '@/components/profile/DangerZoneSection';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, firebaseUser, customClaims, signOut, linkGoogleAccount, unlinkGoogleAccount } = useAuth();
  const [transactions, setTransactions] = useState<LoyaltyTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  // Check if user is a barber - show barber profile instead
  const userRole = customClaims?.role || user?.role;
  if (userRole === 'barber') {
    return <BarberProfilePage />;
  }

  // Check if user is a guest
  const isGuest = userRole === 'guest' || firebaseUser?.isAnonymous;

  // State for points config from database
  const [pointsConfig, setPointsConfig] = useState({
    pointsPerService: 1,
    noShowPenalty: -3,
    expiredPenalty: -3,
    lateCancellationPenalty: -5,
  });

  // State for delete account modal
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

  useEffect(() => {
    if (!firebaseUser) return;

    // Load loyalty transactions
    const loadTransactions = async () => {
      try {
        const transactionsRef = collection(db, 'loyaltyTransactions');
        const q = query(
          transactionsRef,
          where('userId', '==', firebaseUser.uid),
          orderBy('createdAt', 'desc'),
          limit(50)
        );

        const snapshot = await getDocs(q);
        const txs = snapshot.docs.map(doc => ({
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
        })) as LoyaltyTransaction[];

        setTransactions(txs);
      } catch (error) {
        console.error('Error loading transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, [firebaseUser]);

  // Load points configuration from database
  useEffect(() => {
    const configQuery = query(collection(db, 'queue_config'));

    const unsubscribe = onSnapshot(configQuery, (snapshot) => {
      if (!snapshot.empty) {
        const configDoc = snapshot.docs[0];
        const configData = configDoc.data();

        setPointsConfig({
          pointsPerService: configData.loyalty?.pointsPerService || 1,
          noShowPenalty: configData.penalties?.noShow || -3,
          expiredPenalty: configData.penalties?.lateArrival || -3,
          lateCancellationPenalty: configData.penalties?.lateCancellation || -5,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !firebaseUser) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('profilePage.selectValidImage'));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('profilePage.imageTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setCropImageSrc(reader.result as string);
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const uploadCroppedPhoto = async (blob: Blob) => {
    if (!firebaseUser) return;
    setCropImageSrc(null);
    setUploadingPhoto(true);
    const toastId = toast.loading(t('common.uploading'));

    try {
      const storageRef = ref(storage, `users/${firebaseUser.uid}/profile_${Date.now()}.jpg`);
      const uploadTask = uploadBytesResumable(storageRef, blob);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          toast.loading(`${progress.toFixed(0)}%`, { id: toastId });
        },
        (error) => {
          console.error('Error uploading photo:', error);
          toast.error(t('profilePage.photoUploadError'), { id: toastId });
          setUploadingPhoto(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            photoURL: downloadURL,
            updatedAt: new Date(),
          });

          toast.success(t('profilePage.photoUpdated'), { id: toastId });
          setUploadingPhoto(false);
          window.location.reload();
        }
      );
    } catch (error) {
      console.error('Error in photo upload:', error);
      toast.error(t('profilePage.photoUploadError'), { id: toastId });
      setUploadingPhoto(false);
    }
  };

  const handleLinkGoogle = async () => {
    setLinkingGoogle(true);
    try {
      await linkGoogleAccount();
      toast.success(t('profilePage.googleLinked'));
    } catch (error: any) {
      console.error('Error linking Google:', error);
      toast.error(error.message || t('errors.unknown'));
    } finally {
      setLinkingGoogle(false);
    }
  };

  const handleUnlinkGoogle = async () => {
    try {
      await unlinkGoogleAccount();
      toast.success(t('profilePage.googleUnlinked'));
    } catch (error: any) {
      console.error('Error unlinking Google:', error);
      toast.error(error.message || t('errors.unknown'));
    }
  };

  const hasGoogleLinked = firebaseUser?.providerData.some(
    provider => provider.providerId === 'google.com'
  );
  const hasPasswordLinked = firebaseUser?.providerData.some(
    provider => provider.providerId === 'password'
  );

  // Handle account deletion (soft delete)
  const handleDeleteAccount = async () => {
    if (!firebaseUser) return;

    try {
      // Call Cloud Function to perform soft delete
      const deleteAccount = httpsCallable(functions, 'deleteClientAccount');
      await deleteAccount({});

      toast.success(t('deleteAccount.success'));

      // Sign out the user
      await signOut();

      // Redirect to home page
      navigate('/');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      toast.error(t('deleteAccount.error'));
      throw error; // Re-throw to let modal handle error state
    }
  };

  // Guest profile view - simplified with option to create account.
  // Checked BEFORE the !user guard: an anonymous guest has a firebaseUser
  // immediately but their Firestore doc loads async — they must never see
  // the "sign in" screen while already signed in (GuestProfileView only
  // needs firebaseUser).
  if (isGuest) {
    return <GuestProfileView />;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <div className="text-center">
          <h2 className="font-sans text-2xl font-semibold tracking-tight text-ink mb-4">{t('profilePage.signIn')}</h2>
          <p className="text-sm text-ink-muted mb-4">{t('profilePage.mustSignIn')}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-accent text-on-accent px-6 py-2 rounded-lg hover:bg-accent-hover transition-colors focus:outline-none focus:ring-2 focus:ring-focus"
          >
            {t('profilePage.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <ProfileHeader
          user={user}
          uploadingPhoto={uploadingPhoto}
          fileInputRef={fileInputRef}
          onPhotoSelect={handlePhotoUpload}
          onSignOut={signOut}
        />

        {/* Linked Accounts Section */}
        <LinkedAccountsSection
          user={user}
          firebaseUser={firebaseUser}
          hasGoogleLinked={hasGoogleLinked}
          hasPasswordLinked={hasPasswordLinked}
          linkingGoogle={linkingGoogle}
          onLinkGoogle={handleLinkGoogle}
          onUnlinkGoogle={handleUnlinkGoogle}
        />

        {/* Edit Profile Section */}
        <EditProfileSection
          user={user}
          firebaseUser={firebaseUser}
          hasPasswordLinked={hasPasswordLinked}
        />

        {/* Notification Preferences */}
        <NotificationPreferencesSection user={user} firebaseUser={firebaseUser} />

        {/* Appearance - Large Text Toggle (accessibility) */}
        <LargeTextToggleCard
          className="bg-surface border border-hairline rounded-xl p-6 mb-6"
        />

        {/* Loyalty Points */}
        <LoyaltyPointsCard queuePoints={user.queuePoints} />

        {/* Queue History Summary */}
        <QueueHistorySummary queueHistory={user.queueHistory} />

        {/* Loyalty Transactions History */}
        <LoyaltyTransactionsHistory transactions={transactions} loading={loading} />

        {/* Info Card */}
        <PointsInfoCard pointsConfig={pointsConfig} />

        {/* Delete Account Section - Only for clients */}
        {userRole === 'client' && (
          <DangerZoneSection onDeleteClick={() => setShowDeleteAccountModal(true)} />
        )}
      </div>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccount}
        userName={user.displayName || user.email || t('profilePage.userFallback')}
        queuePoints={user.queuePoints || 0}
        isVIP={user.isVIP || false}
        totalCompletedServices={user.queueHistory?.totalCompleted || 0}
      />

      <ImageCropModal
        isOpen={!!cropImageSrc}
        imageSrc={cropImageSrc || ''}
        onConfirm={uploadCroppedPhoto}
        onClose={() => setCropImageSrc(null)}
      />
    </div>
  );
}
